import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import {
  Avatar, Box, Button,
  List, ListItem,
  TextField, Typography,
} from "@mui/material";
import profile from "../../assets/images/profile_img.png";
import { convertUserImage } from "../../util/utils";
import apiServices from "../services/apiServices";
import ButtonOutlinedCentered from "../../components/ButtonOutlined";
import LoadingModal from "../../components/LoadingModal";
import Separator from "../../components/Separator";
import { colors } from "../../common/colors";
import AdminEditPageFrame from "../components/AdminEditPageFrame";
import Section from "../components/editors/Section";
import { styles as inputStyles } from "../components/editors/Styles";
import SubmitButton from "../components/editors/SubmitButton";
import NoticeBanner from "../components/NoticeBanner";
import AddUserModal from "../components/editors/AddUserModal";
import { UserEntity } from "../util/editors";

const displayNoticeTime = 3000;

type Inputs = {
  users: UserEntity[];
  title: string;
  summary: string;
  details: string;
}
type Errors = {
  users: string;
  title: string;
  summary: string;
  details: string;
}

export default function CreateUserNotification(): ReactElement {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /* 入力関連 */
  const [inputs, setInputs] = useState<Inputs>({
    users: [],
    title: "",
    summary: "",
    details: ""
  });
  const [errors, setErrors] = useState<Errors>({
    users: "",
    title: "",
    summary: "",
    details: ""
  });

  /* 表示制御 */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  /* API関連 */
  const sendNotification = async () => {
    if (Object.values(errors).some((error: string) => error.length > 0)) return false;
    setIsLoading(true);

    const body = {
      notification: {
        title: inputs.title,
        summary: inputs.summary,
        details: inputs.details,
        user_notifications_attributes: inputs.users.map((user: any) => ({user_id: user.userId}))
      }
    };

    try {
      await apiServices.sendUserNotification(body);
    } catch (error: any) {
      dispatch(setErrorObj(error))
      return false
    } finally {
      setIsLoading(false);
    }

    setSuccessMessage(T("mng_user_notifications_send_success"));
    setTimeout(() => {
      navigate("/mng/public_users/user_notifications");
    }, displayNoticeTime);

    return true
  }

  const validate = async () => {
    const _errors: Errors = JSON.parse(JSON.stringify(errors));
    if (inputs.users.length === 0) {
      _errors.users = `${T("mng_user_notifications_message_to")}${T("error_enter")}`;
    }
    if (inputs.title.length === 0) {
      _errors.title = `${T("mng_user_notifications_message_title")}${T("error_enter")}`;
    }
    if (inputs.summary.length === 0) {
      _errors.summary = `${T("mng_user_notifications_message_summary")}${T("error_enter")}`;
    }
    if (inputs.details.length === 0) {
      _errors.details = `${T("mng_user_notifications_message_details")}${T("error_enter")}`;
    }
    setErrors(_errors);

    return Object.values(_errors).every((error: string) => error.length === 0);
  }

  /* イベント */
  const addUser = (user: UserEntity) => {
    if (inputs.users.find((u: UserEntity) => u.userId === user.userId)) return;

    setInputs((prev: Inputs) => ({
      ...prev,
      users: [...prev.users, {...user, _destroy: 0}]
    }));
    resetError("users");
  }

  const deleteUser = (user: UserEntity) => {
    setInputs((prev: Inputs) => ({
      ...prev,
      users: prev.users.filter((u: UserEntity) => u.userId !== user.userId)
    }));
  }

  const changeText = (e: any, key: keyof Inputs) => {
    const _inputs: Inputs = JSON.parse(JSON.stringify(inputs));
    _inputs[key] = e.target.value;
    setInputs(_inputs);
    resetError(key);
  }

  const resetError = (key: keyof Errors) => {
    const _errors: Errors = JSON.parse(JSON.stringify(errors));
    _errors[key] = "";
    setErrors(_errors);
  }

  return (
    <AdminEditPageFrame>
      <LoadingModal visible={isLoading} />

      <Box sx={styles.inputs.container}>
        <Box sx={styles.inputs.users.container}>
          <Box sx={styles.inputs.users.title}>
            <Typography variant="body2" color="textPrimary" fontFamily="mplus-1c-bold">
              {`${T("mng_user_notifications_message_to")}${T("required")}`}
            </Typography>
            <Typography variant="body2" sx={styles.inputs.users.count}>
              {inputs.users.length}
            </Typography>
          </Box>

          <Separator color={colors.farm_green200} />

          <List sx={styles.inputs.users.list}>
            {inputs.users.slice().reverse().map((user: UserEntity, index: number) => (
              <ListItem sx={styles.inputs.users.listItem} key={index}>
                <Avatar
                  src={user?.image ? convertUserImage(user?.image) : profile}
                  alt={user?.nickname || ""}
                  sx={styles.inputs.users.icon}
                />
                <Typography sx={styles.inputs.users.name}>
                  {user?.nickname}
                </Typography>
                <Button onClick={() => deleteUser(user)} sx={styles.inputs.users.remove}>
                  <img alt={user?.nickname || ""} src={require("../../assets/images/remove_user.png")} />
                </Button>
              </ListItem>
            ))}
          </List>
          {errors.users && <Typography style={inputStyles.errorText}>{ errors.users }</Typography>}

          <ButtonOutlinedCentered
            title={T("add_users")}
            onClick={() => setShowAddUserModal(true)}
          />

          {showAddUserModal && (
            <AddUserModal
              label={T("mng_user_notifications_message_to")}
              initialUsers={inputs.users}
              includeNavigator={false}
              onAdd={addUser}
              onDelete={deleteUser}
              onClose={() => setShowAddUserModal(false)}
            />
          )}
        </Box>

        <Section
          title={`${T("mng_user_notifications_message_title")}${T("required")}`}
          body={
            <TextField
              value={inputs.title}
              error={!!errors.title}
              helperText={errors.title}
              onChange={(e) => changeText(e, "title")}
              hiddenLabel
              sx={ inputStyles.inputField }
              FormHelperTextProps={{ style: inputStyles.errorOnInputField }}
            />
          }
        />

        <Section
          title={`${T("mng_user_notifications_message_summary")}${T("required")}`}
          body={
            <TextField
              value={inputs.summary}
              error={!!errors.summary}
              helperText={errors.summary}
              onChange={(e) => changeText(e, "summary")}
              hiddenLabel
              sx={ inputStyles.inputField }
              FormHelperTextProps={{ style: inputStyles.errorOnInputField }}
            />
          }
        />

        <Section
          title={`${T("mng_user_notifications_message_details")}${T("required")}`}
          body={
            <TextField
              value={inputs.details}
              error={!!errors.details}
              helperText={errors.details}
              onChange={(e) => changeText(e, "details")}
              hiddenLabel multiline rows={4}
              sx={ inputStyles.inputField }
              FormHelperTextProps={{ style: inputStyles.errorOnInputField }}
            />
          }
        />
      </Box>

      <SubmitButton
        title={T("mng_user_notifications_send")}
        validate={validate}
        onSubmit={sendNotification}
      />

      <NoticeBanner
        open={Boolean(successMessage)}
        messageList={[successMessage]}
        type={"success"}
        style={styles.banner}
        onClose={() => setSuccessMessage("")}
      />

    </AdminEditPageFrame>
  );
}

const styles = {
  inputs: {
    container: {
      backgroundColor: colors.farm_gray15,
      p: 2,
      marginBottom: 1
    },
    users: {
      container: {
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 1,
        padding: 2,
        backgroundColor: colors.farm_gray10,
      },
      title: {
        display: "flex",
        alignItems: "center",
        marginBottom: "4px"
      },
      count: {
        backgroundColor: colors.farm_green500,
        borderRadius: "4px",
        color: colors.farm_white,
        padding: "2px 8px",
        marginLeft: "4px"
      },
      list: {
        display: "flex",
        flexWrap: "wrap",
        gap: 1
      },
      listItem: {
        width: "auto",
        p: 1,
        flexShrink: 0,
        borderRadius: "2px",
        border: `1px solid ${colors.farm_green200}`,
        backgroundColor: "#f9f9f9"
      },
      icon: {
        marginRight: 1
      },
      name: {
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0, // Allows text to shrink and ellipsis
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      remove: {
        minWidth: 30,
        padding: 0,
        borderRadius: 50,
        "& img": {
          borderRadius: 50,
          height: 30
        }
      }
    },
  },

  banner: {
    width: "95%",
  },
};
